import { ConfirmationModal } from "@platform/design-system";
import { useCallback } from "react";
import { logger } from "@platform/browser-logger";
import { useQueryClient } from "@tanstack/react-query";

import {
  useDeleteProposalPlanGroup,
  useDismissPlaceholderProposal,
} from "../../../../api/proposalPlanGroupMutations";
import { proposalQueryKeys } from "../../../../api/useProposals";

import type { PlaceholderMaterializeTarget } from "../../../../adapters/proposalToGrid";

type QuotesSBSDeletePlanDialogProps = {
  planGroupIds: string[];
  placeholderDismissTargets: PlaceholderMaterializeTarget[];
  open: boolean;
  onClose: () => void;
  carrierName: string;
  rfpId: string;
};

export const QuotesSbsDeletePlanGroupDialog: React.FC<
  QuotesSBSDeletePlanDialogProps
> = ({
  planGroupIds,
  placeholderDismissTargets,
  open,
  onClose,
  carrierName,
  rfpId,
}) => {
  const queryClient = useQueryClient();
  const deletePlanGroup = useDeleteProposalPlanGroup();
  const dismissPlaceholder = useDismissPlaceholderProposal();

  const onSuccess = useCallback(async () => {
    try {
      await Promise.all([
        ...planGroupIds.map((id) => {
          logger.info(`[PlanDesigner] Deleting plan group ${id}`);
          return deletePlanGroup.mutateAsync({ id });
        }),
        ...placeholderDismissTargets.map((target) => {
          logger.info(
            `[PlanDesigner] Dismissing placeholder ${target.rfpBenefitPlanGroupId}`,
          );
          return dismissPlaceholder.mutateAsync({ rfpId, target });
        }),
      ]);
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
      onClose();
    } catch {
      // no need to do anything here since the error is already handled
      // within the mutation query
    }
  }, [
    deletePlanGroup,
    dismissPlaceholder,
    onClose,
    placeholderDismissTargets,
    planGroupIds,
    queryClient,
    rfpId,
  ]);

  return (
    <ConfirmationModal
      open={open}
      onCancel={onClose}
      onAccept={onSuccess}
      title="Delete Plan?"
      description={`Are you sure you want to delete ${carrierName} plan? You can access it in Quote History later.`}
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      ConfirmButtonProps={{ variant: "danger" }}
      CancelButtonProps={{ variant: "tertiary" }}
    />
  );
};
